// PlanSubscription.js
import React, { useState, useEffect } from 'react';
import './PlanSubscription.css'; // Import the new CSS file
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useWebSocket } from './WebSocketContext';

const PlanSubscription = () => {

  const [loading, setLoading] = useState(false); // State to track loading status
  const navigate = useNavigate();
  // Initialize Stripe using loadStripe
  const stripeKey = process.env.REACT_APP_STRIPE_API_KEY;
  const stripePromise = loadStripe(stripeKey);
  

  const handleCancelSubscription = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Check if API URL is configured
      if (!process.env.REACT_APP_API_URL) {
        throw new Error('API URL not configured. Please contact support.');
      }
      
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      
      // Create a session to redirect the customer to the Stripe portal
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/subscription/manage`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
  
      if (response.data.url) {
        // Add a small delay to ensure loading state is visible
        setTimeout(() => {
          window.location.href = response.data.url;
        }, 100);
      } else {
        console.error('No URL in response:', response.data);
        alert('Failed to redirect to the subscription management portal. Please try again later.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error redirecting to the Stripe portal:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        alert('Subscription management service not found. Please contact support.');
      } else if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
      } else if (error.message.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        alert('Network error. Please check your internet connection and try again.');
      } else {
        alert(`An error occurred while attempting to access subscription management: ${error.message}`);
      }
      setLoading(false);
    }
  };

  const handleCheckout = async (priceId) => {
    setLoading(true); // Start loading when checkout is initiated
    try {
      // Get the Stripe instance after the promise resolves
      const stripe = await stripePromise;

      if (!stripe) {
        console.error('Stripe failed to load');
        alert('Stripe failed to load. Please try again later.');
        return;
      }

      // Get user session to retrieve JWT token
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();

      // Create a checkout session on the server
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/checkout`,
        { priceId, allowPromotionCodes: true },
        {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
          },
        }
      );

      // Redirect to Stripe Checkout
      const { sessionId } = response.data;
      const result = await stripe.redirectToCheckout({ sessionId });

      if (result.error) {
        console.error(result.error.message);
        alert('Error during checkout. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start the checkout process. Please try again.');
    }finally {
        setLoading(false); // Stop loading once the process completes
    }
  };

  return (
    <div className="plan-subscription-container">
      <div className="plan-subscription-content">
        {/* Modern Header */}
        <div className="subscription-header">
          <h1 className="subscription-title">Choose Your Plan</h1>
          <p className="subscription-subtitle">Unlock the full potential of your travel planning</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-spinner">
            Processing your request...
          </div>
        )}

        {/* Modern Plans Grid */}
        <div className="plans-grid">
          {/* Adventurer Plan */}
          <div className="plan-card">
            <h3 className="plan-name">Adventurer</h3>
            <img 
              src="/images/beach.jpeg" 
              alt="Adventurer Plan - Travel Adventures" 
              className="plan-image" 
            />
            <p className="plan-description">
              Perfect for weekend warriors and short trips. Includes up to 500 notes for planning your next adventure and up to 10 active journeys on your Explorer Board.
            </p>
            <div className="plan-price">
              $2.99
              <span className="plan-price-period">/month</span>
            </div>
            <button
              className="plan-button"
              onClick={() => handleCheckout(process.env.REACT_APP_STRIPE_Enhanced_Price_ID)}
              disabled={loading}
            >
              Choose Adventurer
            </button>
          </div>

          {/* Globe Trotter Plan */}
          <div className="plan-card featured">
            <h3 className="plan-name">Globe Trotter</h3>
            <img 
              src="/images/plane.jpg" 
              alt="Globe Trotter Plan - World Travel" 
              className="plan-image" 
            />
            <p className="plan-description">
              For the serious traveler. Create unlimited notes and get enhanced AI recommendations with advanced trip planning features. Plus, create custom Pack-It itineraries for tailored travel experiences, along with all of the features of the Adventurer plan along with priority support.
            </p>
            <div className="plan-price">
              $4.99
              <span className="plan-price-period">/month</span>
            </div>
            <button
              className="plan-button featured"
              onClick={() => handleCheckout(process.env.REACT_APP_STRIPE_Premium_Price_ID)}
              disabled={loading}
            >
              Choose Globe Trotter
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="back-button"
            onClick={() => navigate('/account-settings?settings=true')}
            disabled={loading}
          >
            ← Back to Settings
          </button>
          <a
            href="#"
            className="manage-subscription-link"
            onClick={handleCancelSubscription}
            style={{ 
              pointerEvents: loading ? 'none' : 'auto', 
              opacity: loading ? 0.5 : 1 
            }}
          >
            Manage Existing Subscription
          </a>
        </div>
      </div>
    </div>
  );
};

export default PlanSubscription;
